import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  Button,
  ContentPanel,
  ErrorState,
  Skeleton,
} from "@oms/ui";
import { useQuery } from "@tanstack/react-query";
import { Download, SlidersHorizontal } from "lucide-react";
import { PageShell } from "@/components/pages/page-shell";
import { SearchField } from "@/components/ui/search-field";
import { OrdersTable, PAGE_SIZE } from "@/components/tables/orders-table";
import { OrderStatusTabs } from "@/components/orders/order-status-tabs";
import { ordersApi } from "@/lib/api";
import {
  orderQueryKeys,
  ordersLiveQueryOptions,
} from "@/lib/orders";
import { parseOrdersSearch, type OrdersSearch } from "@/lib/orders-search";

export const Route = createFileRoute("/app/orders")({
  validateSearch: parseOrdersSearch,
  component: OrdersPage,
});

function OrdersPage() {
  const navigate = useNavigate({ from: "/app/orders" });
  const { status, search, page } = Route.useSearch();

  const countsQuery = useQuery({
    queryKey: orderQueryKeys.counts(),
    queryFn: async () => {
      const response = await ordersApi.counts();
      return response.data ?? { all: 0 };
    },
    ...ordersLiveQueryOptions,
  });

  const ordersQuery = useQuery({
    queryKey: orderQueryKeys.list(status, search, page),
    queryFn: async () => {
      const response = await ordersApi.list({
        status,
        search,
        page,
        pageSize: PAGE_SIZE,
      });
      return {
        items: response.data ?? [],
        total: response.meta?.total ?? 0,
        totalPages: Math.max(
          1,
          Math.ceil((response.meta?.total ?? 0) / PAGE_SIZE),
        ),
      };
    },
    ...ordersLiveQueryOptions,
  });

  const updateSearch = (patch: Partial<OrdersSearch>) => {
    void navigate({
      search: (prev) => ({
        ...prev,
        ...patch,
        page:
          patch.page ??
          (patch.status !== undefined || patch.search !== undefined
            ? 1
            : prev.page),
      }),
    });
  };

  const totalAll = countsQuery.data?.all ?? 0;
  const filteredTotal = ordersQuery.data?.total ?? 0;
  const totalPages = ordersQuery.data?.totalPages ?? 1;
  const items = ordersQuery.data?.items ?? [];

  return (
    <PageShell className="space-y-6">
      <ContentPanel>
        <div className="space-y-4 border-b border-border-muted px-5 py-4 sm:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="min-w-0">
              <h1 className="text-lg font-semibold tracking-tight text-text">
                Orders
              </h1>
              <p className="mt-0.5 text-sm text-text-muted">
                {filteredTotal} of {totalAll} orders
              </p>
            </div>

            <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
              <SearchField
                value={search}
                onChange={(event) =>
                  updateSearch({ search: event.target.value, page: 1 })
                }
                className="w-full sm:w-56"
                placeholder="Search orders..."
                aria-label="Search orders"
              />
              <div className="flex items-center gap-2">
                <Button variant="secondary" size="sm">
                  <SlidersHorizontal className="h-4 w-4" aria-hidden />
                  Filters
                </Button>
                <Button variant="secondary" size="sm">
                  <Download className="h-4 w-4" aria-hidden />
                  Export
                </Button>
              </div>
            </div>
          </div>

          <OrderStatusTabs
            value={status}
            onChange={(value) => updateSearch({ status: value, page: 1 })}
          />
        </div>

        {ordersQuery.isLoading ? (
          <div className="space-y-2 px-5 py-4 sm:px-6">
            {Array.from({ length: PAGE_SIZE }).map((_, index) => (
              <Skeleton key={index} className="h-12 rounded-lg" />
            ))}
          </div>
        ) : null}

        {ordersQuery.isError ? (
          <div className="px-5 py-10 sm:px-6">
            <ErrorState
              description="Could not load orders."
              onRetry={() => void ordersQuery.refetch()}
            />
          </div>
        ) : null}

        {!ordersQuery.isLoading && !ordersQuery.isError ? (
          <OrdersTable
            orders={items}
            page={page}
            totalPages={totalPages}
            totalResults={filteredTotal}
            onPageChange={(nextPage) => updateSearch({ page: nextPage })}
            hasFilters={status !== "all" || search.trim().length > 0}
          />
        ) : null}
      </ContentPanel>
    </PageShell>
  );
}
